import { formatDate } from "@angular/common";
import { S25DateParse } from "../../util/core/s25.date.parse";
import { S25DateformatConst } from "./s25.dateformat.const";
import { S25BasicUtil } from "../../util/s25-basic-util";
import { Nullable } from "../../pojo/Util";
import { Preference } from "../../pojo/PeferenceI";
import Pref = Preference.Pref;

export class S25Datefilter {
    private static DATE_FORMATS_SPLIT: RegExp =
        /((?:[^yMNnFfdHhmsaZEw}']+)|(?:'(?:[^']|'')*')|(?:E+|y+|M+|N+|n+|F+|f+|d+|H+|h+|m+|s+|a|Z|w+))(.*)/;
    private static DATETIME_FORMATS: any = {
        SHORTMONTH: "Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec".split(","),
        SHORTDAY: "Sun,Mon,Tue,Wed,Thu,Fri,Sat".split(","),
    };

    private static DATE_FORMATS: any = {
        NNN: S25Datefilter.dateStrGetter("Month", true, "toUpperCase"),
        nnn: S25Datefilter.dateStrGetter("Month", true, "toLowerCase"),
        FFF: S25Datefilter.dateStrGetter("Day", true, "toUpperCase"),
        fff: S25Datefilter.dateStrGetter("Day", true, "toLowerCase"),
    };

    private static TIME_FORMATS: any = {
        a: function (value: string) {
            return value.replace(new RegExp("AM", "g"), "am").replace(new RegExp("PM", "g"), "pm");
        },
    };

    private static parseFormat(format: Nullable<string>) {
        // get format tokens out of given format
        let parts: Array<string> = [];

        while (format) {
            const match = this.DATE_FORMATS_SPLIT.exec(format);
            if (match) {
                parts = parts.concat(Array.prototype.slice.call(match, 1));
                format = parts.pop();
            } else {
                parts.push(format);
                format = null;
            }
        }

        return parts;
    }

    private static dateStrGetter(name: string, isShortForm: boolean, postFormat: string) {
        return function (date: any, formats: any) {
            const value = date["get" + name]();
            const get = (isShortForm ? "SHORT" + name : name).toUpperCase();
            let result = formats[get][value];

            if (postFormat) {
                result = result[postFormat]();
            }

            return result;
        };
    }

    public static transform(date: Date | Exclude<string, ""> | Exclude<number, 0>, format: string): string;
    public static transform(date: undefined | null, format: string): undefined;
    public static transform(date: Date | string | number | undefined | null, format: string): string | undefined;
    public static transform(date: Date | string | number | undefined | null, format: string) {
        if (!date) {
            return;
        }

        try {
            let isS25dateFormat: boolean = false;

            // determine if format is defined and starts with 's25|'
            if (format && format.indexOf(S25DateformatConst.S25_DATE_FORMAT_PREFIX) === 0) {
                isS25dateFormat = true;
                format = format.substring(S25DateformatConst.S25_DATE_FORMAT_PREFIX.length);
            }

            // make sure date is Date() object; DateFilter returns original String when passed String,
            //    but we must apply the filter format, so we need Date()
            // angular datepicker-popup has a bug which sets ngModel.viewValue as a String the first time popup is created
            // http://stackoverflow.com/questions/24198669/angular-bootsrap-datepicker-date-format-does-not-format-ng-model-value/26427583#26427583
            if (typeof date === "string") {
                date = S25DateParse.date(date);
            }

            // call parent, get formatted result
            let result = formatDate(date, format, "en-US");

            // ANG-7351 Replace &nnbsp; with &nbsp;
            result = result.replace(/\u202f/g, "\u00A0");

            // if failed -nothing can be done, return
            if (!result) {
                return result;
            }

            // if format starts with 's25|' - replace result with extended format options
            if (isS25dateFormat) {
                const parts = S25Datefilter.parseFormat(format);

                // for each format token - if is it contained in Extra formats - replace result with extra formatted value
                S25BasicUtil.forEach(parts, function (key: string) {
                    let fn = S25Datefilter.DATE_FORMATS[key];
                    if (fn) {
                        result = result.replace(new RegExp(key, "g"), fn(date, S25Datefilter.DATETIME_FORMATS));
                    }
                    fn = S25Datefilter.TIME_FORMATS[key];
                    if (fn) {
                        result = fn(result);
                    }
                });
            }

            return result;
        } catch (ex) {
            return null;
        }
    }

    public static DEFAULT_DATE_PREFERENCES: { [key: string]: Pref } = {
        CalendarView: {
            value: "1",
            type: "B",
        },
        DateOrder: {
            value: "DMY",
            type: "B",
        },
        DateSep: {
            value: "S",
            type: "B",
        },
        Day: {
            value: "U",
            type: "B",
        },
        Month: {
            value: "I",
            type: "B",
        },
    };
}
